import type { ColumnDef, ColumnType } from "../columns";

/**
 * Canonical identifier for a filter operator. The set is intentionally a
 * closed union: a column type either uses one of these, or it ships its own
 * via `column.customOperators`. New ids must be added here so consumers can
 * exhaust over them in a switch.
 */
export type OperatorId =
  // Text family
  | "contains"
  | "startsWith"
  | "endsWith"
  // Shared single-value
  | "equals"
  // Numeric family
  | "gt"
  | "gte"
  | "lt"
  | "lte"
  | "between"
  // Date / datetime family
  | "on"
  | "before"
  | "after"
  | "today"
  | "last7"
  | "last30"
  | "last90"
  // Categorical (enum / boolean / relation)
  | "in"
  | "notIn"
  // Universal "no value" presets
  | "isEmpty"
  | "isNotEmpty";

/**
 * Shape of the value(s) the user must supply for an operator.
 *
 * - `single` — one input (one Input, one date, one option).
 * - `range` — two inputs (low / high). Used by `between`.
 * - `multi` — N inputs / multi-select. Used by `in` / `notIn`.
 *
 * Operators with `requiresValue: false` ignore this field; convention is to
 * keep it set to `"single"` so callers don't need to special-case it.
 */
export type OperatorValueShape = "single" | "range" | "multi";

/**
 * One operator definition. The shape is a pure data record; UI components
 * consume it to render the right controls and to label the chip in the
 * filter bar. Adding behaviour here would couple operator data to the UI
 * layer — keep it dumb.
 */
export interface OperatorDef {
  /** Stable identifier (used as filter state key). */
  id: OperatorId;
  /** Human-readable label. Plain English; i18n routing is the caller's job. */
  label: string;
  /** When `false` the operator stands alone (e.g. `isEmpty`). */
  requiresValue: boolean;
  /** Layout shape of the value input(s). See {@link OperatorValueShape}. */
  valueShape: OperatorValueShape;
}

/**
 * Internal helper: build the freezable text-family operator list.
 *
 * Kept as a function so we don't hand-write the same object twice for the
 * three numeric variants (number/money/percent) and the three categorical
 * variants (enum/boolean/relation). Returning fresh array instances would
 * defeat referential equality checks the tests rely on, so each consumer
 * receives the same memoised list.
 */
const TEXT_OPERATORS: readonly OperatorDef[] = Object.freeze([
  { id: "contains", label: "Contains", requiresValue: true, valueShape: "single" },
  { id: "equals", label: "Equals", requiresValue: true, valueShape: "single" },
  { id: "startsWith", label: "Starts with", requiresValue: true, valueShape: "single" },
  { id: "endsWith", label: "Ends with", requiresValue: true, valueShape: "single" },
  { id: "isEmpty", label: "Is empty", requiresValue: false, valueShape: "single" },
  { id: "isNotEmpty", label: "Is not empty", requiresValue: false, valueShape: "single" },
]);

const NUMERIC_OPERATORS: readonly OperatorDef[] = Object.freeze([
  { id: "equals", label: "Equals", requiresValue: true, valueShape: "single" },
  { id: "gt", label: "Greater than", requiresValue: true, valueShape: "single" },
  { id: "gte", label: "Greater or equal", requiresValue: true, valueShape: "single" },
  { id: "lt", label: "Less than", requiresValue: true, valueShape: "single" },
  { id: "lte", label: "Less or equal", requiresValue: true, valueShape: "single" },
  { id: "between", label: "Between", requiresValue: true, valueShape: "range" },
  { id: "isEmpty", label: "Is empty", requiresValue: false, valueShape: "single" },
]);

const DATE_OPERATORS: readonly OperatorDef[] = Object.freeze([
  { id: "on", label: "On", requiresValue: true, valueShape: "single" },
  { id: "before", label: "Before", requiresValue: true, valueShape: "single" },
  { id: "after", label: "After", requiresValue: true, valueShape: "single" },
  { id: "between", label: "Between", requiresValue: true, valueShape: "range" },
  { id: "today", label: "Today", requiresValue: false, valueShape: "single" },
  { id: "last7", label: "Last 7 days", requiresValue: false, valueShape: "single" },
  { id: "last30", label: "Last 30 days", requiresValue: false, valueShape: "single" },
  { id: "last90", label: "Last 90 days", requiresValue: false, valueShape: "single" },
  { id: "isEmpty", label: "Is empty", requiresValue: false, valueShape: "single" },
]);

const CATEGORICAL_OPERATORS: readonly OperatorDef[] = Object.freeze([
  { id: "equals", label: "Equals", requiresValue: true, valueShape: "single" },
  { id: "in", label: "Is any of", requiresValue: true, valueShape: "multi" },
  { id: "notIn", label: "Is none of", requiresValue: true, valueShape: "multi" },
  { id: "isEmpty", label: "Is empty", requiresValue: false, valueShape: "single" },
]);

const EMPTY_OPERATORS: readonly OperatorDef[] = Object.freeze([]);

/**
 * Lookup table mapping each {@link ColumnType} to its operator list.
 *
 * - `number`, `money`, `percent` share the numeric operator set verbatim.
 * - `date`, `datetime` share the temporal operator set verbatim.
 * - `enum`, `boolean`, `relation` share the categorical operator set.
 * - `custom` returns an empty list; callers provide operators per-column
 *   via `column.customOperators`.
 *
 * The arrays are frozen so consumers cannot accidentally mutate the shared
 * reference. Casting to mutable on read is by design — callers always treat
 * these as readonly even though the public type is plain `OperatorDef[]`.
 */
export const OPERATORS_BY_TYPE: Record<ColumnType, OperatorDef[]> = {
  text: TEXT_OPERATORS as OperatorDef[],
  number: NUMERIC_OPERATORS as OperatorDef[],
  money: NUMERIC_OPERATORS as OperatorDef[],
  percent: NUMERIC_OPERATORS as OperatorDef[],
  date: DATE_OPERATORS as OperatorDef[],
  datetime: DATE_OPERATORS as OperatorDef[],
  enum: CATEGORICAL_OPERATORS as OperatorDef[],
  boolean: CATEGORICAL_OPERATORS as OperatorDef[],
  relation: CATEGORICAL_OPERATORS as OperatorDef[],
  custom: EMPTY_OPERATORS as OperatorDef[],
};

/**
 * A column extension contract: callers wanting custom operators on a
 * `type: "custom"` column attach them via `customOperators`. The contract is
 * stamped on `ColumnDef` only loosely (via intersection) to avoid bloating
 * the base column shape with filter-engine concerns; the typed lookup helper
 * below is the one place in the engine where this property is read.
 */
export interface ColumnWithCustomOperators {
  customOperators?: OperatorDef[];
}

/**
 * Resolve the operator list for a {@link ColumnType}. For `custom` you'll get
 * an empty list — use {@link getOperatorsForColumn} when you have access to
 * the column itself (which can carry `customOperators`).
 */
export function getOperatorsForType(type: ColumnType): OperatorDef[] {
  return OPERATORS_BY_TYPE[type];
}

/**
 * Resolve the operator list for a column.
 *
 * For `type: "custom"` the column may declare its own list via
 * `column.customOperators`. When that is omitted, the result is `[]` and the
 * filter UI should render a "no operators configured for this column" empty
 * state rather than crashing.
 */
export function getOperatorsForColumn<TRow>(
  column: ColumnDef<TRow> & ColumnWithCustomOperators,
): OperatorDef[] {
  if (column.type === "custom") {
    return column.customOperators ?? [];
  }
  return OPERATORS_BY_TYPE[column.type];
}

/**
 * Lookup a single operator by id within the operator list for a given type.
 *
 * Returns `undefined` when:
 * - the type is `custom` (use {@link getOperatorsForColumn} + Array.find),
 * - the id is not part of that type's vocabulary (e.g. `between` on text).
 */
export function getOperatorById(
  type: ColumnType,
  id: OperatorId,
): OperatorDef | undefined {
  const list = OPERATORS_BY_TYPE[type];
  return list.find((op) => op.id === id);
}
